<!-- Meta usage of the month: free service-message allowance (per number) + other billed categories -->
<div class="card mb-3">
  <div class="card-body">

    <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
      <div>
        <h5 class="mt-0 mb-1">{{ 'channel.health.metaUsage.title' | translate }}</h5>
        @if (usage && !loadingContent && !failed) {
          <p class="text-muted small mb-0">{{ subtitle() }}</p>
        }
      </div>
      @if (usage && !loadingContent && !failed) {
        <span class="badge campaign-badge text-nowrap" [ngClass]="sourceBadgeClass()">{{ sourceLabel() }}</span>
      }
    </div>

    @if (loadingContent) {
      <div class="d-flex justify-content-center py-3">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ 'dashboard.dashboard.loading' | translate }}</span>
        </div>
      </div>
    }

    @if (!loadingContent && failed) {
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span class="text-muted">{{ 'channel.health.metaUsage.unavailable' | translate }}</span>
        <button type="button" class="btn btn-sm btn-outline-primary" (click)="load()">
          <i class="ri-refresh-line me-1"></i>
          {{ 'attendance.copilot.retry' | translate }}
        </button>
      </div>
    }

    @if (!loadingContent && !failed && usage) {
      <div class="row g-4">

        <!-- Allowance meter + details -->
        <div [ngClass]="showSideColumn() ? 'col-lg-6' : 'col-12'">
          <div class="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-1">
            <span class="fs-4 fw-bold">{{ usedLabel() }}</span>
            <span class="text-muted">{{ remainingLabel() }} · {{ percentLabel() }}</span>
          </div>
          <div
            class="progress progress-lg mb-3"
            role="progressbar"
            [attr.aria-label]="'channel.health.metaUsage.progressLabel' | translate"
            [attr.aria-valuenow]="usage.serviceAllowance.used"
            aria-valuemin="0"
            [attr.aria-valuemax]="usage.serviceAllowance.limit"
          >
            <div class="progress-bar" [ngClass]="progressBarClass()" [style.width.%]="progressWidth()"></div>
          </div>

          @if (statusAlert(); as alert) {
            <div class="alert d-flex align-items-start gap-2" [ngClass]="alert.cssClass" role="alert">
              <i [ngClass]="alert.icon"></i>
              <span>{{ alert.text }}</span>
            </div>
          }

          @if (usage.source === 'PLATFORM') {
            <div class="alert alert-secondary small" role="alert">
              <i class="ri-information-line me-1"></i>
              {{ 'channel.health.metaUsage.platformFallback' | translate }}
            </div>
          }

          @if (usage.scope === 'WABA') {
            <p class="text-muted small">{{ 'channel.health.metaUsage.scopeWaba' | translate }}</p>
          }

          <dl class="row small mb-0">
            <dt class="col-8 fw-normal text-muted">{{ 'channel.health.metaUsage.billed' | translate }}</dt>
            <dd class="col-4 text-end fw-semibold mb-2">{{ billedLabel() }}</dd>

            @if (usage.source === 'META') {
              <dt class="col-8 fw-normal text-muted">
                {{ 'channel.health.metaUsage.freeEntryPoint' | translate }}
                <span class="d-block">{{ 'channel.health.metaUsage.freeEntryPointHint' | translate }}</span>
              </dt>
              <dd class="col-4 text-end fw-semibold mb-2">{{ formatNumber(usage.service.freeEntryPointVolume) }}</dd>
            }

            <dt class="col-8 fw-normal text-muted">
              {{ 'channel.health.metaUsage.platformEstimate' | translate }}
              <i
                class="ri-information-line ms-1"
                tabindex="0"
                [matTooltip]="'channel.health.metaUsage.platformEstimateHint' | translate"
                [attr.aria-label]="'channel.health.metaUsage.platformEstimateHint' | translate"
              ></i>
            </dt>
            <dd class="col-4 text-end fw-semibold mb-2">{{ formatNumber(usage.platformEstimate.serviceDelivered) }}</dd>
          </dl>
        </div>

        <!-- What Meta charged this number this month so far — total + per category (Meta source only) -->
        @if (showMonthSpend()) {
          <div class="col-lg-6">
            <div class="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-1">
              <span class="text-muted">{{ 'channel.health.metaUsage.totalSpend' | translate }}</span>
              <span class="fs-4 fw-bold">{{ monthTotalCostLabel() }}</span>
            </div>
            <p class="text-muted small mb-2">
              {{ 'channel.health.metaUsage.totalSpendHint' | translate: { start: formatDate(usage.period.start) } }}
            </p>
            @if (costUnavailable()) {
              <div class="alert alert-secondary small py-2" role="alert">
                <i class="ri-information-line me-1"></i>
                {{ 'channel.health.metaUsage.costUnavailable' | translate }}
              </div>
            }
            <div class="table-responsive">
              <table class="table table-sm table-centered table-hover mb-2">
                <thead class="table-light">
                  <tr>
                    <th>{{ 'channel.health.qualityTab.templatesCategory' | translate }}</th>
                    <th class="text-end">{{ 'channel.health.summary.chartDelivered' | translate }}</th>
                    <th class="text-end">{{ 'channel.health.metaUsage.colBilled' | translate }}</th>
                    <th class="text-end text-nowrap">
                      {{ 'channel.health.metaUsage.colCostMonth' | translate }}
                      <i
                        class="ri-information-line ms-1"
                        tabindex="0"
                        [matTooltip]="'channel.health.metaUsage.colCostHint' | translate: { start: formatDate(usage.period.start) }"
                        [attr.aria-label]="'channel.health.metaUsage.colCostHint' | translate: { start: formatDate(usage.period.start) }"
                      ></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of usage.categories; track row.category) {
                    <tr>
                      <td>{{ categoryLabel(row.category) }}</td>
                      <td class="text-end">{{ formatNumber(row.volume) }}</td>
                      <td class="text-end">{{ formatNumber(row.billableVolume) }}</td>
                      <td class="text-end">{{ formatCost(row.cost) }}</td>
                    </tr>
                  }
                </tbody>
                @if (usage.monthTotal; as total) {
                  <tfoot>
                    <tr class="fw-bold">
                      <td>{{ 'channel.health.metaUsage.totalRow' | translate }}</td>
                      <td class="text-end">{{ formatNumber(total.volume) }}</td>
                      <td class="text-end">{{ formatNumber(total.billableVolume) }}</td>
                      <td class="text-end">{{ formatCost(total.cost) }}</td>
                    </tr>
                  </tfoot>
                }
              </table>
            </div>
            <p class="text-muted small mb-0">
              {{ 'channel.health.metaUsage.utilityNote' | translate }}
            </p>
          </div>
        }

        <!-- No spend from Meta (cost refused for a partner-billed WABA, or Meta unread): service messages at the client's rate -->
        @if (showServiceEstimate() && usage.serviceEstimate; as estimate) {
          <div class="col-lg-6">
            <div class="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-1">
              <span class="text-muted">{{ 'channel.health.metaUsage.estimate.title' | translate }}</span>
              <span class="fs-4 fw-bold">{{ formatBrl(estimate.amount) }}</span>
            </div>
            <p class="text-muted small mb-2">
              {{ 'channel.health.metaUsage.estimate.hint' | translate: { start: formatDate(usage.period.start) } }}
            </p>

            <div class="table-responsive">
              <table class="table table-sm table-centered mb-2">
                <thead class="table-light">
                  <tr>
                    <th>{{ 'channel.health.metaUsage.estimate.colDelivered' | translate }}</th>
                    <th class="text-end">{{ 'channel.health.metaUsage.estimate.colMessages' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ 'channel.health.metaUsage.estimate.total' | translate }}</td>
                    <td class="text-end fw-semibold">{{ formatNumber(estimate.total) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-3">
                      − {{ 'channel.health.metaUsage.estimate.free' | translate }}
                      <span class="d-block small text-muted ps-2">{{ 'channel.health.metaUsage.estimate.freeHint' | translate }}</span>
                    </td>
                    <td class="text-end">{{ formatNumber(estimate.freeCustomerService) }}</td>
                  </tr>
                  <tr>
                    <td class="ps-3">
                      − {{ 'channel.health.metaUsage.estimate.ctwa' | translate }}
                      @if (usage.source === 'PLATFORM') {
                        <span class="d-block small text-muted ps-2">{{ 'channel.health.metaUsage.estimate.ctwaUnknown' | translate }}</span>
                      } @else {
                        <span class="d-block small text-muted ps-2">{{ 'channel.health.metaUsage.estimate.ctwaHint' | translate }}</span>
                      }
                    </td>
                    <td class="text-end">{{ usage.source === 'PLATFORM' ? '—' : formatNumber(estimate.freeEntryPoint) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="fw-bold">
                    <td>
                      {{ 'channel.health.metaUsage.estimate.paid' | translate }}
                      <span class="d-block small fw-normal text-muted">{{ paidFormulaLabel() }}</span>
                    </td>
                    <td class="text-end">
                      {{ formatNumber(estimate.paid) }}
                      <span class="d-block small">{{ formatBrl(estimate.amount) }}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p class="text-muted small mb-0">
              <i class="ri-information-line me-1"></i>
              {{ (estimate.unitPriceSource === 'WALLET'
                ? 'channel.health.metaUsage.estimate.priceWallet'
                : 'channel.health.metaUsage.estimate.priceDefault') | translate: { price: formatBrl(estimate.unitPrice) } }}
              {{ 'channel.health.metaUsage.estimate.disclaimer' | translate }}
            </p>
          </div>
        }
      </div>

      @if (usage.source === 'META') {
        <p class="text-muted small mt-3 mb-0">
          <i class="ri-time-line me-1"></i>
          {{ 'channel.health.metaUsage.disclaimer' | translate }}
        </p>
      }
    }
  </div>
</div>
